/**
 * Application entry point.
 *
 * ORDER MATTERS HERE. The host is installed before anything that logs, and the
 * global error handlers are installed before the first render — an error thrown
 * during the initial render is otherwise the one error nobody ever sees.
 */

import "./styles/index.css"

import { analytics, ANALYTICS_ON, createWebProviders } from "@anthra/platform/analytics"
import { installHost } from "@anthra/platform/host"
import { webHost } from "@anthra/platform/host/web"
import { installGlobalErrorHandlers, logger } from "@anthra/platform/logger"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { ErrorBoundary } from "@/components/errors/ErrorBoundary"

import { routeTree } from "./routeTree.gen"

// FIRST. Everything below this line may log, and the logger asks the host for
// its configuration.
installHost(webHost())

installGlobalErrorHandlers()

// Analytics is switched off (ANALYTICS_ON in @anthra/platform/analytics).
if (ANALYTICS_ON) {
	analytics.register(...createWebProviders())
	void analytics.init().catch((error: unknown) => {
		logger.warn("analytics failed to initialise", {}, error)
	})
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// A 401 or a 404 will not become a 200 on the third attempt; retrying
			// them wastes three round trips before the user sees the error.
			retry: (failureCount, error: unknown) => {
				const status = (error as { status?: number }).status
				if (status !== undefined && status >= 400 && status < 500) return false
				return failureCount < 3
			},
			retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30_000),
			staleTime: 30_000,
			refetchOnWindowFocus: true,
		},
		mutations: {
			// NOT retried. A mutation is not idempotent unless it carries an
			// idempotency key, and a retried payment is a double charge
			// (API_STYLE_GUIDE.md §7).
			retry: 0,
		},
	},
})

const router = createRouter({
	routeTree,
	context: { queryClient },
	// The router waits this long before showing a pending state, so a fast
	// navigation does not flash a spinner — which reads as slower than no
	// spinner at all.
	defaultPendingMs: 300,
	defaultPreload: "intent",
})

declare module "@tanstack/react-router" {
	// An INTERFACE, and it must be. This is module augmentation: TanStack Router
	// declares `Register` as an interface, and only interfaces merge. Declaring
	// it as a `type` is a duplicate identifier, not an override.
	//
	// consistent-type-definitions prefers `type` everywhere else, and is right to.
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Register {
		router: typeof router
	}
}

const container = document.getElementById("root")
if (!container) throw new Error("#root is missing from index.html")

createRoot(container).render(
	<StrictMode>
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
			</QueryClientProvider>
		</ErrorBoundary>
	</StrictMode>,
)
